// ============================================================================
// CONFIG SETTINGS SCREEN
// ============================================================================
// Admin entry point for hierarchical config (F3).
//
//   Left rail: config groups (tabs)              ← ConfigGroupSidebar
//   Right pane: scope selector + field list + save/discard footer
//                                                 ← ConfigSettingsPanel
//
// All state + API calls flow through useConfigSettings(); this screen owns
// only unsaved-changes + save/discard confirmations.
// ============================================================================

import React, { useCallback, useRef, useState } from 'react';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useConfigSettings } from 'hooks/api/useConfigSettings';
import type { ConfigScopeType } from 'api/types/config.types';
import ConfigGroupSidebar from './ConfigGroupSidebar';
import ConfigSettingsPanel from './ConfigSettingsPanel';
import './config-module.css';

const ConfigSettingsScreen: React.FC = () => {
  const {
    groups,
    groupsLoading,
    definitions,
    effectiveEntries,
    loading,
    scopes,
    scopesLoading,

    activeGroupKey,
    activeScopeType,
    activeScopeId,

    editedValues,
    hasChanges,
    saving,

    selectGroup,
    selectScopeType,
    selectScopeTarget,

    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  } = useConfigSettings();

  const [navConfirmOpen, setNavConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const guarded = useCallback(
    (action: () => void) => {
      if (hasChanges) {
        pendingActionRef.current = action;
        setNavConfirmOpen(true);
      } else {
        action();
      }
    },
    [hasChanges],
  );

  const handleGroupSelect = useCallback(
    (groupKey: string) => {
      if (groupKey === activeGroupKey) return;
      guarded(() => selectGroup(groupKey));
    },
    [activeGroupKey, guarded, selectGroup],
  );

  const handleScopeTypeChange = useCallback(
    (scopeType: ConfigScopeType) => {
      if (scopeType === activeScopeType) return;
      guarded(() => selectScopeType(scopeType));
    },
    [activeScopeType, guarded, selectScopeType],
  );

  const handleScopeIdChange = useCallback(
    (scopeId: string | null) => {
      if (scopeId === activeScopeId) return;
      guarded(() => selectScopeTarget(scopeId));
    },
    [activeScopeId, guarded, selectScopeTarget],
  );

  const activeGroup = groups.find((g) => g.groupKey === activeGroupKey) ?? null;

  return (
    <div className="module-master-container">
      <div className="config-settings-container">
        <ConfigGroupSidebar
          groups={groups}
          activeGroupKey={activeGroupKey}
          onGroupSelect={handleGroupSelect}
          loading={groupsLoading}
        />
        <ConfigSettingsPanel
          activeGroup={activeGroup}
          definitions={definitions}
          effectiveEntries={effectiveEntries}
          loading={loading}
          activeScopeType={activeScopeType}
          onScopeTypeChange={handleScopeTypeChange}
          activeScopeId={activeScopeId}
          onScopeIdChange={handleScopeIdChange}
          scopes={scopes}
          scopesLoading={scopesLoading}
          editedValues={editedValues}
          onValueChange={updateValue}
          onResetValue={resetValue}
          hasChanges={hasChanges}
          saving={saving}
          onSave={() => setSaveConfirmOpen(true)}
          onDiscard={() => setDiscardConfirmOpen(true)}
        />
      </div>

      {/* Navigation guard — fires when user switches group/scope with unsaved edits */}
      <ConfirmDialog
        open={navConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Discard them and continue?"
        confirmLabel="Discard & Continue"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          discardChanges();
          setNavConfirmOpen(false);
          pendingActionRef.current?.();
          pendingActionRef.current = null;
        }}
        onCancel={() => {
          setNavConfirmOpen(false);
          pendingActionRef.current = null;
        }}
      />

      {/* Explicit save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Configuration"
        message="Save these configuration changes?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={saving}
        loadingMessage="Saving…"
        onConfirm={async () => {
          await saveChanges();
          setSaveConfirmOpen(false);
        }}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Explicit discard confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes"
        message="Discard all unsaved changes?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          discardChanges();
          setDiscardConfirmOpen(false);
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </div>
  );
};

export default ConfigSettingsScreen;
